import { writable, get, type Readable } from 'svelte/store';
import { browser } from '$app/environment';

export interface Persisted<T> extends Readable<T> {
  /** Sets the value of the internal store and serializes the value to localStorage */
  set(value: T): void;
  /** Returns the value from the internal store */
  get(): T;
  /** Run an update `fn` on the value of the internal store and update the internal store with the returned value */
  update(fn: (value: T) => T): void;
  /** Resets the value to the initial value passed in on creation of the store */
  clear(): void;
}

export function createPersistedStore<T = unknown>(key: string, initialValue: T): Persisted<T> {
  const { subscribe, set } = writable(initialValue);

  // Set the value of the store from LS
  if (browser) {
    const item = window.localStorage.getItem(key);
    if (item) {
      set(JSON.parse(item));
    }
  }

  const store: Persisted<T> = {
    subscribe,
    set: (value: T) => {
      set(value);
      if (browser) {
        window.localStorage.setItem(key, JSON.stringify(value));
      }
    },
    get: () => {
      return get(store);
    },
    update: fn => {
      const result = get(store);
      const value = fn(result);
      store.set(value);
    },
    clear: () => {
      set(initialValue);
      if (browser) {
        window.localStorage.removeItem(key);
      }
    }
  };
  return store;
}
